import { PropsWithChildren, createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

import { Alert } from '@zooplus/ui-components';

export type ErrorMessage = {
  message: string;
  retryable?: boolean;
  variant?: 'caution' | 'error' | 'info' | 'success' | 'warning';
};

type ErrorContextData = {
  error?: ErrorMessage;
  setError: (errorMessage?: ErrorMessage) => void;
};

// Provided default values are only there for typescript to be happy.
// They will never be used.
const ErrorContext = createContext<ErrorContextData>({
  setError: () => {},
});

export const useErrorContext = () => useContext(ErrorContext);

type ErrorContextProviderProps = {
  errorContainerId: string;
};

/**
 * Context providing a 'setError' function.
 *
 * Because the components are often embedded in another UI, we need to display any error happening in our app in that external UI, to improve clarity to the user. Hence the use of a portal.
 *
 * Exceptions are:
 * - individual validation error of an input: the message is displayed alongside the input
 * - errors happening when one of our modal is shown: we display ourselves the error in the modal instead of in the portal, because the modal is usually hiding the portal element.
 */
const ErrorContextProvider: React.FC<PropsWithChildren<ErrorContextProviderProps>> = ({
  errorContainerId,
  children,
}) => {
  const [error, setError] = useState<ErrorMessage>();

  return (
    <ErrorContext.Provider value={{ error, setError }}>
      <ErrorContainerPortal errorContainerId={errorContainerId}>
        {error && (
          <Alert className="pp-mb-m" data-zta="errorCreditCard" variant={error?.variant}>
            {error?.message}
          </Alert>
        )}
      </ErrorContainerPortal>
      {children}
    </ErrorContext.Provider>
  );
};

export default ErrorContextProvider;

type ErrorContainerPortalProps = {
  errorContainerId: string;
};

const ErrorContainerPortal: React.FC<PropsWithChildren<ErrorContainerPortalProps>> = ({
  children,
  errorContainerId,
}) => {
  const errorContainer = document.getElementById(errorContainerId);
  if (!errorContainer) {
    return null;
  }
  return <>{createPortal(children, errorContainer)}</>;
};
